/**
 * services/filterapi.js
 * Filter-specific API calls — matches the Go backend CollegeHandler exactly.
 * All endpoints use: http://localhost:5001/api/v1
 */

import axios from "axios";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "http://localhost:5001/api")
  .replace(/\/+$/, "")
  .replace(/\/api$/, "") + "/api/v1";

// ─── STATES (used in CollegeFilter Location section) ──────────────────────────
// Backend: GET /api/v1/colleges/filters/states
// Returns: { success: true, states: [{ _id: "Maharashtra", count: 42 }, ...] }
export const getAvailableStates = async () => {
  try {
    const res = await axios.get(`${API_BASE}/colleges/filters/states`);
    return res.data;
  } catch (err) {
    console.error("getAvailableStates error:", err);
    return { success: false, states: [] };
  }
};

// ─── CITIES BY STATE ──────────────────────────────────────────────────────────
// Backend: GET /api/v1/colleges/filters/cities?state=Maharashtra
// Returns: { success: true, cities: [{ _id: "Mumbai", count: 10 }, ...] }
export const getCitiesByState = async (state) => {
  try {
    const res = await axios.get(`${API_BASE}/colleges/filters/cities`, {
      params: { state }
    });
    return res.data;
  } catch (err) {
    console.error("getCitiesByState error:", err);
    return { success: false, cities: [] };
  }
};

// ─── AVAILABLE COURSES / DISCIPLINES ─────────────────────────────────────────
// Backend: GET /api/v1/colleges/filters/courses
// Returns: { success: true, courses: [{ _id: "B.Tech CSE", count: 55 }, ...] }
export const getAvailableCourses = async () => {
  try {
    const res = await axios.get(`${API_BASE}/colleges/filters/courses`);
    return res.data;
  } catch (err) {
    console.error("getAvailableCourses error:", err);
    return { success: false, courses: [] };
  }
};

// ─── BUILD QUERY PARAMS for getColleges() ─────────────────────────────────────
// Converts the filter object from CollegeFilter into API query params
// matching exactly what the Go backend expects.
export const buildFilterParams = (filters = {}, extra = {}) => {
  const params = { ...extra };

  // disciplines → courses (backend field: "courses" = course names)
  if (filters.disciplines?.length) {
    params.courses = filters.disciplines.join(",");
  }

  // countries → countries
  if (filters.countries?.length) {
    params.countries = filters.countries.join(",");
  }

  // states → states
  if (filters.states?.length) {
    params.states = filters.states.join(",");
  }

  // continents → continents
  if (filters.continents?.length) {
    params.continents = filters.continents.join(",");
  }

  // tuitionFee → fee_min, fee_max  (backend reads fee_min and fee_max separately)
  if (filters.tuitionFee?.min) {
    params.fee_min = filters.tuitionFee.min;
  }
  if (filters.tuitionFee?.max) {
    params.fee_max = filters.tuitionFee.max;
  }

  // durations → durations (backend: courses.duration regex match)
  if (filters.durations?.length) {
    params.durations = filters.durations.join(",");
  }

  // format → format
  if (filters.format?.length) {
    params.format = filters.format.join(",");
  }

  // attendance → attendance
  if (filters.attendance?.length) {
    params.attendance = filters.attendance.join(",");
  }

  // degreeTypes → degreeTypes
  if (filters.degreeTypes?.length) {
    params.degreeTypes = filters.degreeTypes.join(",");
  }

  // specialProgrammes → specialProgrammes
  if (filters.specialProgrammes?.length) {
    params.specialProgrammes = filters.specialProgrammes.join(",");
  }

  return params;
};

// ─── PSYCHOMETRIC TEST ────────────────────────────────────────────────────────
// Public endpoint — no auth required
// Backend: GET /api/v1/psychometric/tests  (public route)
export const getPsychometricTests = async () => {
  try {
    const res = await axios.get(`${API_BASE}/psychometric/tests`);
    return res.data;
  } catch (err) {
    console.error("getPsychometricTests error:", err);
    return { success: false, tests: [] };
  }
};

// ─── QUICK LINKS (PUBLIC) ────────────────────────────────────────────────────
// Correct public endpoint — does NOT require admin auth
// Used in Hero.jsx quick links section
export const getPublicQuickLinks = async () => {
  try {
    // Try public endpoint first
    const res = await axios.get(`${API_BASE}/quick-links/public`);
    return res.data?.data || res.data || [];
  } catch (err) {
    // Fallback: try without /public
    try {
      const res2 = await axios.get(`${API_BASE}/quick-links`);
      return res2.data?.data || res2.data || [];
    } catch {
      // Return empty — Hero will use default static links
      return [];
    }
  }
};